import React, { useState, useEffect } from 'react';
import { useFetch, useScrollRestore } from '../../hooks/useFetch';
import { showGlobalToast, Spinner, ErrorBanner, EmptyState, Modal, ConfirmDialog, ImportButton, SearchableSelect } from '../../components/ui';
import AddKitModal from './AddKit';
import EditProductModal from '../products/EditProduct';
import { errMsg } from '../../utils/format';
import api, { downloadFile } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

function EditKitModal({ kit, onClose, onSaved, onOpenAlert, onDelete, sync, syncing }) {
  const { data: products } = useFetch('/api/products');
  const [form, setForm] = useState({
    name: kit.name || '',
    sku: kit.sku || '',
    shopifyProductId: kit.shopify_product_id || '',
    amazonAsin: kit.amazon_asin || '',
  });
  const [components, setComponents] = useState(
    (kit.components || []).map(c => ({ productId: String(c.productId), quantity: String(c.quantity) }))
  );
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addComponent = () => setComponents(c => [...c, { productId: '', quantity: '1' }]);
  const removeComponent = (idx) => setComponents(c => c.filter((_, j) => j !== idx));
  const setComp = (idx, k, v) => setComponents(c => c.map((row, j) => j === idx ? { ...row, [k]: v } : row));

  const productOptions = (products || []).map(p => ({
    value: p.id,
    label: `${p.sku} — ${p.name} (on hand: ${p.quantity_on_hand})`
  }));

  const save = async (e) => {
    e.preventDefault();
    const valid = components.filter(c => c.productId && c.quantity);
    if (!valid.length) { alert('Add at least one component'); return; }
    setSaving(true);
    try {
      await api.patch(`/api/kits/${kit.id}`, {
        name: form.name,
        sku: form.sku || undefined,
        shopifyProductId: form.shopifyProductId || null,
        amazonAsin: form.amazonAsin || null,
      });
      await api.put(`/api/kits/${kit.id}/components`, {
        components: valid.map(c => ({ productId: parseInt(c.productId), quantity: parseInt(c.quantity) }))
      });
      onSaved(); onClose();
    } catch (err) { alert(errMsg(err)); }
    finally { setSaving(false); }
  };

  // Find linked product info for display
  const linkedProduct = kit.linked_product_id
    ? (products || []).find(p => p.id === kit.linked_product_id)
    : null;

  return (
    <Modal title={`Edit Bundle — ${kit.name}`} onClose={onClose} wide
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" form="edit-kit-form" type="submit">{saving ? 'Saving…' : 'Save'}</button></>}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-sm" onClick={() => { onClose(); onOpenAlert(kit); }}>
          <i className="ti ti-bell" /> Alert threshold
        </button>
        <button type="button" className="btn btn-sm btn-danger" onClick={() => { onClose(); onDelete(kit); }}>
          <i className="ti ti-trash" /> Delete bundle
        </button>
      </div>
      {linkedProduct && (
        <div style={{ background: 'var(--bg-secondary)', borderRadius: 6, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>
          <span style={{ color: 'var(--text-tertiary)' }}>Linked product: </span>
          <strong>{linkedProduct.name}</strong>
        </div>
      )}
      <form id="edit-kit-form" onSubmit={save}>
        <div className="form-group">
          <label>Bundle name *</label>
          <input required value={form.name} onChange={e => set('name', e.target.value)} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Shopify Product ID</label>
            <input value={form.shopifyProductId} onChange={e => set('shopifyProductId', e.target.value)} placeholder="Optional" />
          </div>
          <div className="form-group">
            <label>Amazon ASIN</label>
            <input value={form.amazonAsin} onChange={e => set('amazonAsin', e.target.value)} placeholder="Optional" />
          </div>
        </div>

        <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 8, marginTop: 16 }}>Components</div>
        {components.map((comp, idx) => (
          <div key={idx} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 90px auto', gap: 8, marginBottom: 8, alignItems: 'end' }}>
            <div className="form-group" style={{ margin: 0 }}>
              {idx === 0 && <label style={{ fontSize: 11 }}>Finished good</label>}
              <SearchableSelect
                value={comp.productId}
                onChange={v => setComp(idx, 'productId', v)}
                options={productOptions}
                placeholder="— Search or select product —"
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              {idx === 0 && <label style={{ fontSize: 11 }}>Qty per bundle</label>}
              <input type="number" min="1" value={comp.quantity} onChange={e => setComp(idx, 'quantity', e.target.value)} />
            </div>
            <button type="button" className="btn btn-sm btn-danger"
              style={{ marginTop: idx === 0 ? 18 : 0 }} onClick={() => removeComponent(idx)}>
              <i className="ti ti-x" />
            </button>
          </div>
        ))}
        <button type="button" className="btn btn-sm" onClick={addComponent} style={{ marginTop: 4 }}>
          <i className="ti ti-plus" /> Add component
        </button>
      </form>
    </Modal>
  );
}

function AlertThresholdModal({ kit, onClose, onSaved }) {
  const [form, setForm] = useState({
    alertsEnabled: kit.alerts_enabled !== false,
    threshold: kit.low_buildable_threshold ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.patch(`/api/kits/${kit.id}`, {
        alertsEnabled: form.alertsEnabled,
        lowBuildableThreshold: parseInt(form.threshold) || 0,
      });
      onSaved(); onClose();
    }
    catch (err) { alert(errMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={`Alert threshold — ${kit.name}`} onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" form="kit-alert-form" type="submit">{saving ? 'Saving…' : 'Save'}</button></>}>
      <form id="kit-alert-form" onSubmit={save}>
        <div className="form-group" style={{ display:'flex', alignItems:'center', gap:10 }}>
          <input type="checkbox" id="kit-alerts-enabled" style={{ width:'auto' }}
            checked={form.alertsEnabled}
            onChange={e => setForm(f => ({ ...f, alertsEnabled: e.target.checked }))} />
          <label htmlFor="kit-alerts-enabled" style={{ marginBottom:0, cursor:'pointer' }}>
            Alerts enabled for this bundle
          </label>
        </div>
        <small style={{ fontSize:11, color:'var(--text-tertiary)', display:'block', marginBottom:12 }}>
          When enabled, a zero-buildable alert fires automatically when buildable qty reaches 0.
        </small>
        <div className="form-group">
          <label>Low buildable quantity threshold</label>
          <input type="number" min="0" value={form.threshold} onChange={e => setForm(f => ({ ...f, threshold: e.target.value }))} />
          <small style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
            Warning fires when buildable qty ≤ this number. Set to 0 for zero-buildable alert only.
          </small>
        </div>
      </form>
    </Modal>
  );
}

function ComponentRow({ comp, onOpenProduct, indent = 1 }) {
  const [expanded, setExpanded] = useState(false);
  const hasSubComponents = comp.isKit && comp.subComponents?.length > 0;
  const canBuild = comp.isKit
    ? (comp.subBuildableQty ?? '—')
    : Math.floor((comp.onHand || 0) / (comp.quantity || 1));
  const indentPx = indent * 20;

  return (
    <>
      <tr
        onClick={() => hasSubComponents ? setExpanded(e => !e) : onOpenProduct(comp.productId)}
        style={{ cursor: 'pointer', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}
      >
        {/* chevron column */}
        <td style={{ textAlign: 'center' }}>
          {hasSubComponents
            ? <i className={`ti ${expanded ? 'ti-chevron-down' : 'ti-chevron-right'}`} style={{ fontSize: 11, color: 'var(--text-tertiary)' }} />
            : null}
        </td>
        {/* name column — indent via inline spacer */}
        <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          <span style={{ display: 'inline-block', width: indentPx }} />
          {comp.name}
          {comp.isKit && <span className="badge badge-blue" style={{ marginLeft: 6, fontSize: 9 }}>Bundle</span>}
        </td>
        {/* SKU */}
        <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-tertiary)' }}>{comp.sku}</td>
        {/* Source — blank for components */}
        <td></td>
        {/* Components count — show qty per kit */}
        <td style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>×{comp.quantity}</td>
        {/* Buildable qty */}
        <td style={{ fontSize: 13, fontWeight: 500 }}>{comp.isKit ? (comp.subBuildableQty ?? '—') : (comp.onHand ?? '—')}</td>
        {/* Alert — show can-build */}
        <td style={{ fontSize: 12, color: parseInt(canBuild) === 0 ? 'var(--color-danger)' : 'var(--text-tertiary)' }}>
          {canBuild} buildable
        </td>
        {/* Shopify, Amazon — blank */}
        <td></td><td></td>
      </tr>
      {expanded && hasSubComponents && comp.subComponents.map((sub, i) => (
        <tr key={i}
          onClick={() => onOpenProduct(sub.productId)}
          style={{ cursor: 'pointer', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}
        >
          <td></td>
          <td style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            <span style={{ display: 'inline-block', width: indentPx + 20 }} />
            {sub.name}
          </td>
          <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-tertiary)' }}>{sub.sku}</td>
          <td></td>
          <td style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>×{sub.quantity}</td>
          <td style={{ fontSize: 12 }}>{sub.onHand ?? '—'}</td>
          <td style={{ fontSize: 11, color: parseInt(sub.canBuild) === 0 ? 'var(--color-danger)' : 'var(--text-tertiary)' }}>
            {sub.canBuild} buildable
          </td>
          <td></td><td></td>
        </tr>
      ))}
    </>
  );
}

function KitRow({ k, indent, isManager, setEditModal, getAlertStatus, saveScroll, expandedComponents, toggleComponents, onOpenProduct }) {
  const alertStatus = getAlertStatus(k);
  const rowBg = alertStatus === 'zero' ? 'var(--red-light)' : alertStatus === 'low' ? 'var(--amber-light)' : undefined;
  const componentsExpanded = !!expandedComponents[k.id];
  const components = k.components || [];

  return (
    <>
      <tr style={{ cursor: 'pointer', ...(rowBg ? { background: rowBg } : {}) }}>
        <td onClick={() => toggleComponents(k.id)}>
          <i className={`ti ${componentsExpanded ? 'ti-chevron-down' : 'ti-chevron-right'}`} style={{ fontSize: 12, color: 'var(--text-tertiary)' }} />
        </td>
        <td style={{ fontWeight: indent ? 400 : 500, paddingLeft: indent ? 16 : undefined }}
          onClick={() => { saveScroll(); setEditModal(k); }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {k.image_url
              ? <img src={k.image_url} alt="" style={{ width:36, height:36, objectFit:'cover', borderRadius:4, flexShrink:0 }} />
              : <div style={{ width:36, height:36, borderRadius:4, background:'var(--surface-2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <i className="ti ti-packages" style={{ fontSize:14, color:'var(--text-tertiary)' }} />
                </div>}
            {k.name}
          </div>
        </td>
        <td style={{ fontFamily: 'monospace', fontSize: 12 }} onClick={() => { saveScroll(); setEditModal(k); }}>{k.sku}</td>
        <td onClick={() => toggleComponents(k.id)}>{components.length} items</td>
        <td style={{ fontWeight: 500 }} onClick={() => { saveScroll(); setEditModal(k); }}>{k.buildable_qty != null ? k.buildable_qty : '—'}</td>
        <td onClick={() => { saveScroll(); setEditModal(k); }}>
          {alertStatus === 'zero' && <span className="badge badge-crit">Cannot build</span>}
          {alertStatus === 'low'  && <span className="badge badge-amber">Low buildable</span>}
          {alertStatus === 'ok'   && <span className="badge badge-ok">OK</span>}
          {!alertStatus && <span className="text-muted" style={{ fontSize: 11 }}>
            {k.low_buildable_threshold > 0 ? `≤ ${k.low_buildable_threshold}` : 'No alert'}
          </span>}
        </td>

      </tr>
      {componentsExpanded && components.map((comp, i) => (
        <ComponentRow key={i} comp={comp} onOpenProduct={onOpenProduct} indent={1} />
      ))}
    </>
  );
}

export default function Kits() {
  const { data, loading, error, refetch } = useFetch('/api/kits');
  const { isManager } = useAuth();
  const { saveScroll, restoreScroll, applyScroll } = useScrollRestore('sf_kits_scroll');
  useEffect(() => { applyScroll(); }, [data]); // eslint-disable-line
  const [expandedComponents, setExpandedComponents] = useState({});
  const [productModal, setProductModal] = useState(null);
  const toggleComponents = (kitId) => setExpandedComponents(s => ({ ...s, [kitId]: !s[kitId] }));
  const onOpenProduct = async (productId) => {
    try {
      const { data: prod } = await api.get(`/api/products/${productId}`);
      setProductModal(prod);
    } catch (err) { alert(errMsg(err)); }
  };
  const [syncing, setSyncing] = useState(null);
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [alertModal, setAlertModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [search, setSearch] = useState(() => sessionStorage.getItem('sf_kits_search') || '');
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('sf_kits_expanded') || '{}'); }
    catch { return {}; }
  });
  const toggleGroup = (key) => setExpandedGroups(e => {
    const next = { ...e, [key]: !e[key] };
    sessionStorage.setItem('sf_kits_expanded', JSON.stringify(next));
    return next;
  });

  const handleCsvImport = async (file) => {
    setImporting(true);
    setImportResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data: res } = await api.post('/api/kits/import-csv', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult({ ...res, isCsvImport: true });
      refetch();
      showGlobalToast(`Imported ${res.created + res.updated} bundle(s)`);
    } catch (err) {
      alert(errMsg(err));
    } finally {
      setImporting(false);
    }
  };

  const sync = async (id) => {
    setSyncing(id);
    try { await api.post(`/api/kits/${id}/sync`); refetch(); }
    catch (err) { alert(errMsg(err)); }
    finally { setSyncing(null); }
  };

  const deleteKit = async (kit) => {
    try { await api.delete(`/api/kits/${kit.id}`); refetch(); }
    catch (err) { alert(errMsg(err)); }
    finally { setDeleteConfirm(null); }
  };

  const getAlertStatus = (kit) => {
    const threshold = parseInt(kit.low_buildable_threshold) || 0;
    const buildable = parseInt(kit.buildable_qty) ?? null;
    if (!threshold || buildable === null) return null;
    if (buildable === 0) return 'zero';
    if (buildable <= threshold) return 'low';
    return 'ok';
  };

  const zeroCount = (data || []).filter(k => getAlertStatus(k) === 'zero').length;
  const lowCount  = (data || []).filter(k => getAlertStatus(k) === 'low').length;

  const filtered = (data || []).filter(k =>
    !search ||
    k.name.toLowerCase().includes(search.toLowerCase()) ||
    k.sku.toLowerCase().includes(search.toLowerCase())
  );

  // Group kits into collapsible rows by shared base name — the text before
  // the first parenthetical variant suffix — same approach as Finished Goods.
  const baseName = (name) => {
    const idx = name.indexOf('(');
    return idx > 0 ? name.slice(0, idx).trim() : name.trim();
  };

  const groups = React.useMemo(() => {
    const map = new Map();
    for (const k of filtered) {
      const key = `name:${baseName(k.name)}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(k);
    }
    const result = [];
    for (const [key, items] of map) {
      if (items.length > 1) {
        result.push({ isGroup: true, key, label: baseName(items[0].name), items });
      } else {
        result.push({ isGroup: false, key, items });
      }
    }
    const order = new Map(filtered.map((k, i) => [k.id, i]));
    result.sort((a, b) => order.get(a.items[0].id) - order.get(b.items[0].id));
    return result;
  }, [filtered]);

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} onRetry={refetch} />;

  return (
    <div>
      {addModal && <AddKitModal onClose={() => setAddModal(false)} onSaved={() => { refetch(); showGlobalToast('Kit created successfully'); }} />}
      {productModal && (
        <EditProductModal
          product={productModal}
          onClose={() => setProductModal(null)}
          onSaved={() => { refetch(); setProductModal(null); }}
          onOpenKit={() => {}}
          onOpenAlert={() => {}}
          onOpenLog={() => {}}
        />
      )}
      {editModal && <EditKitModal kit={editModal} onClose={() => { setEditModal(null); restoreScroll(); }} onSaved={() => { refetch(); showGlobalToast('Kit updated successfully'); }}
        onOpenAlert={(k) => setAlertModal(k)} onDelete={(k) => setDeleteConfirm(k)} sync={sync} syncing={syncing} />}
      {alertModal && <AlertThresholdModal kit={alertModal} onClose={() => setAlertModal(null)} onSaved={() => { refetch(); showGlobalToast('Alert threshold updated'); }} />}
      {deleteConfirm && (
        <ConfirmDialog danger
          message={`Delete kit "${deleteConfirm.name}"? This cannot be undone.`}
          onConfirm={() => deleteKit(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      <div className="page-header">
        <div>
          <h1>Bundles</h1>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-sm" onClick={() => {
              const allCollapsed = {};
              sessionStorage.setItem('sf_kits_expanded', JSON.stringify(allCollapsed));
              setExpandedGroups(allCollapsed);
              setExpandedComponents({});
            }}>
              <i className="ti ti-fold" /> Collapse all
            </button>
            {isManager && (
              <>
                <button className="btn btn-sm" onClick={() => downloadFile('/api/kits/export/csv')}>
                  <i className="ti ti-download" /> Export CSV
                </button>
                <ImportButton accept=".csv" onFile={handleCsvImport} label="Import CSV" />
              </>
            )}
            <button className="btn btn-primary btn-sm" onClick={() => setAddModal(true)}>
              <i className="ti ti-plus" /> New bundle
            </button>
          </div>
        </div>
      </div>

      {importing && (
        <div className="alert alert-info" style={{ marginBottom: 10 }}>
          <i className="ti ti-loader-2" style={{ animation: 'spin 1s linear infinite' }} /> Importing bundles…
        </div>
      )}

      {importResult && (
        <div className={`alert ${importResult.kitComponentsUnmatched?.length ? 'alert-warning' : 'alert-success'}`}
          style={{ marginBottom: 10, flexDirection: 'column', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>
              <strong>Bundle import complete:</strong> {importResult.bundlesProcessed} bundle(s) processed<br/>
              {importResult.bundleProductsCreated} new finished good(s) created for bundles
              {' '}&middot; {importResult.kitsCreated} kit(s) created, {importResult.kitsUpdated} updated
              {' '}&middot; {importResult.bomsCreated} BOM(s) created, {importResult.bomsUpdated} updated<br/>
              {importResult.rawMaterialsCreated} new raw material(s) created, {importResult.rawMaterialsMatched} matched to existing
              {' '}&middot; {importResult.kitComponentsMatched} kit component(s) matched to existing finished goods
            </div>
            <button className="btn btn-sm" onClick={() => setImportResult(null)}><i className="ti ti-x" /></button>
          </div>
          {importResult.kitComponentsUnmatched?.length > 0 && (
            <details style={{ marginTop: 8, width: '100%' }}>
              <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                {importResult.kitComponentsUnmatched.length} SKU'd component(s) could not be matched and were skipped — click to view
              </summary>
              <div style={{ maxHeight: 240, overflowY: 'auto', marginTop: 8 }}>
                <table style={{ width: '100%', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th style={{ textAlign: 'left', padding: '4px 8px' }}>Bundle</th>
                      <th style={{ textAlign: 'left', padding: '4px 8px' }}>Component</th>
                      <th style={{ textAlign: 'left', padding: '4px 8px' }}>SKU</th>
                      <th style={{ textAlign: 'right', padding: '4px 8px' }}>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importResult.kitComponentsUnmatched.map((u, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '4px 8px' }}>{u.bundle}</td>
                        <td style={{ padding: '4px 8px' }}>{u.component}</td>
                        <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{u.sku || '—'}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>{u.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6 }}>
                These had a SKU listed but it didn't match any existing Finished Good by SKU or name. Add them to Finished Goods first, then re-run the import to link them.
              </div>
            </details>
          )}
          {importResult.errors?.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--color-danger)' }}>
              {importResult.errors.length} bundle(s) failed to import: {importResult.errors.map(e => e.bundle).join(', ')}
            </div>
          )}
        </div>
      )}
      {zeroCount > 0 && (
        <div className="alert alert-danger" style={{ marginBottom: 10 }}>
          <i className="ti ti-alert-circle" />
          <strong>{zeroCount} kit{zeroCount > 1 ? 's' : ''} cannot be built</strong>
          &nbsp;— one or more components are out of stock.
        </div>
      )}
      {lowCount > 0 && (
        <div className="alert alert-warning" style={{ marginBottom: 10 }}>
          <i className="ti ti-alert-triangle" />
          <strong>{lowCount} kit{lowCount > 1 ? 's' : ''} below buildable quantity threshold.</strong>
        </div>
      )}

      <div className="filter-bar">
        <div className="search-input">
          <i className="ti ti-search" />
          <input placeholder="Search SKU or bundle name…" value={search}
            onChange={e => { setSearch(e.target.value); sessionStorage.setItem('sf_kits_search', e.target.value); }} />
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 28 }}></th>
                <th>Bundle name</th><th>SKU</th><th>Components</th><th>Buildable qty</th><th>Alert</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => {
                if (!g.isGroup) {
                  const k = g.items[0];
                  return (
                    <KitRow key={k.id} k={k} isManager={isManager}
                      setEditModal={setEditModal} getAlertStatus={getAlertStatus} saveScroll={saveScroll}
                      expandedComponents={expandedComponents} toggleComponents={toggleComponents}
                      onOpenProduct={onOpenProduct} />
                  );
                }
                const expanded = !!expandedGroups[g.key];
                const totalComponents = g.items.reduce((s, k) => s + (k.components?.length || 0), 0);
                const totalBuildable = g.items.reduce((s, k) => s + (parseInt(k.buildable_qty) || 0), 0);
                const anyZero = g.items.some(k => getAlertStatus(k) === 'zero');
                const anyLow = g.items.some(k => getAlertStatus(k) === 'low');
                return (
                  <React.Fragment key={g.key}>
                    <tr onClick={() => toggleGroup(g.key)}
                      style={{ cursor: 'pointer', background: 'var(--bg-secondary)', ...(anyZero ? { background: 'var(--red-light)' } : anyLow ? { background: 'var(--amber-light)' } : {}) }}>
                      <td>
                        <i className={`ti ${expanded ? 'ti-chevron-down' : 'ti-chevron-right'}`} style={{ fontSize: 12, color: 'var(--text-tertiary)' }} />
                      </td>
                      <td style={{ fontWeight: 600 }}>
                        {g.label}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {g.items.length} variant{g.items.length !== 1 ? 's' : ''}
                      </td>
                      <td></td>
                      <td>{totalComponents} items</td>
                      <td style={{ fontWeight: 500 }}>{totalBuildable}</td>
                      <td colSpan={3}></td>
                    </tr>
                    {expanded && g.items.map(k => (
                      <KitRow key={k.id} k={k} indent isManager={isManager}
                        setEditModal={setEditModal} getAlertStatus={getAlertStatus} saveScroll={saveScroll}
                        expandedComponents={expandedComponents} toggleComponents={toggleComponents}
                        onOpenProduct={onOpenProduct} />
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {!filtered.length && <EmptyState icon="ti-packages" message="No bundles defined" />}
        </div>
      </div>
    </div>
  );
}
